import { type FC } from 'preact/compat';
import { useEffect, useRef } from 'preact/hooks';

import clsx from 'clsx';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';

import {
  usePlaydeckActions,
  usePlaydeckState,
  useProgress,
} from '@/entities/playdeck/models/playdeck.store';
import { Method } from '@/entities/playdeck/types';

import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import {
  makeGameSessionClickEvent,
  makeGameSessionStartEvent,
} from '@/shared/service/analytics/events/game-session-start-event';
import { useAppState } from '@/shared/stores/app-store/app-store';
import { ButtonVariant } from '@/shared/ui/button/types';
import { Button } from '@/shared/ui/button/ui';

import { LOADING_TIME_FOR_GAMES__NOT_INTEGRATED } from './constants';

import s from './index.module.scss';

interface Props {
  className?: string;
}

export const PlayButton: FC<Props> = ({ className }) => {
  const t = useIntl();

  const progress = useProgress();

  const { game, startAppArgs } = useAppState();
  const { respond } = usePlaydeckState();

  const { setProgress, setPlaydeckOpen } = usePlaydeckActions();

  const porgressRef = useRef(progress);

  porgressRef.current = progress;

  useEffect(() => {
    setTimeout(() => {
      const zeroProgress = porgressRef.current === 0;
      if (zeroProgress && game?.flags?.noIntegration) {
        setProgress(100);
      }
    }, LOADING_TIME_FOR_GAMES__NOT_INTEGRATED);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayClick = () => {
    if (!respond) return;

    if (progress !== 100) return;

    respond({ method: Method.PLAY });

    closeBottomMenu();
  };

  const closeBottomMenu = () => {
    if (!respond || !game) return;

    AnalyticsService.send(makeGameSessionClickEvent(game.key, startAppArgs?.src));
    AnalyticsService.send(
      makeGameSessionStartEvent(game.key, startAppArgs?.src, 'click')
    );

    setPlaydeckOpen(false);
    respond({ method: Method.IS_OPEN, value: false });
  };

  const isGameLoaded = respond && progress === 100 && !!game;

  return (
    <Button
      className={clsx(s['play-btn'], !isGameLoaded && s.loading, className)}
      loading={!isGameLoaded}
      onClick={handlePlayClick}
      variant={ButtonVariant.volumeButton}
    >
      {isGameLoaded && <span className={s['button-text']}>{t('Play')}</span>}
      <span className={clsx(s['loading-text'], isGameLoaded && s.loaded)}>
        {t('Loading game')}
      </span>
      <span className={s['inner-btn-shadow']} />
    </Button>
  );
};
